import Button from 'tio-ui/components/button';
import { dropTask } from 'ember-concurrency';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import EmailLookupWrapper from 'tio-employee/components/auth/email-lookup-wrapper';
import emailSchema from 'tio-common/validation-schema/models/email';
import { sendForgotPasswordRequest } from 'tio-data/requests/login';
import Input from 'tio-ui/components/input';
import ErrorMessages from 'tio-ui/components/error-messages';
import type Owner from '@ember/owner';
import type RouterService from '@ember/routing/router-service';
import type { Store as StoreService } from '@warp-drive/core';
import { safeParse } from 'valibot';

export default class ForgotPasswordRoute extends Component<{ Args: Record<string, never> }> {
  @service declare store: StoreService;
  @service declare router: RouterService;

  @tracked email = '';
  @tracked isLoading = false;
  @tracked hasSubmitError = false;

  constructor(owner: Owner, args: object) {
    super(owner, args);
    this.email = this.emailFromQueryParams;
  }

  get validationErrors() {
    const result = safeParse(emailSchema, { email: this.email });
    return result.success ? [] : result.issues.map((issue) => issue.message);
  }

  get emailFromQueryParams() {
    const urlParams = new URLSearchParams(window.location.search);
    return decodeURIComponent(urlParams.get('email') || '');
  }

  sendPasswordEmail = dropTask(async () => {
    this.hasSubmitError = false;
    try {
      await this.store.request(sendForgotPasswordRequest(this.email));
      this.router.transitionTo('forgot-password.confirmation');
    } catch (e) {
      console.error(e);
      this.hasSubmitError = true;
    } finally {
      this.isLoading = false;
    }
  });

  get isDisabled() {
    return !this.email || !!this.validationErrors.length;
  }

  submitForgotPassword = async () => {
    this.isLoading = true;
    if (this.validationErrors.length) return;
    this.sendPasswordEmail.perform();
  };

  backToLogin = () => {
    this.router.transitionTo('login');
  };

  <template>
    <div class="tio-col-flush h-screen container mx-auto w-full overflow-auto">
      <EmailLookupWrapper @route="forgot-password">
        <h1 class="text-xl my-4 text-center">{{t "login.forgot_password_question"}}</h1>
        <p class="mb-4">{{t "login.forgot_password_body"}}</p>
        <form {{on "submit" this.submitForgotPassword}} data-test-forgot-password-form>
          <div class="mb-10">
            <Input
              @label={{t "login.email"}}
              @value={{this.email}}
              @onChange={{fn (mut this.email)}}
              @errors={{this.validationErrors}}
              @type="email"
              data-test-email-field
            />
          </div>
          {{#if this.hasSubmitError}}
            <ErrorMessages
              @error={{t "login.forgot_password_error"}}
              @showErrors={{true}}
              data-test-forgot-password-error
            />
          {{/if}}
          <div class="tio-row-center gap-x-4 mb-10">
            <Button @appearance="outlined" {{on "click" this.backToLogin}} data-test-back-button>
              {{t "login.back_to_login"}}
            </Button>
            <Button
              @intent="primary"
              {{on "click" this.submitForgotPassword}}
              @isDisabled={{this.isDisabled}}
              data-test-submit-button
            >
              {{t "common.submit"}}
            </Button>
          </div>
        </form>
      </EmailLookupWrapper>
    </div>
  </template>
}
